import { useState } from "react";
import { Outlet, useLocation, useNavigate } from "react-router-dom";
import {
  Box,
  Activity,
  AlertCircle,
  BarChart3,
  Building2,
  CalendarClock,
  ClipboardCheck,
  ClipboardList,
  Cpu,
  History,
  LayoutDashboard,
  LogOut,
  Menu,
  Package,
  RefreshCcwDot,
  Users,
} from "lucide-react";
import { Button } from "./ui/button";
import { Sidebar, SidebarBrand, SidebarGroup, SidebarNavItem } from "./ui/sidebar";
import { Topbar } from "./ui/topbar";
import { RealtimeBadge } from "./ui/realtime-badge";
import { ThemeToggle } from "./ThemeToggle";
import { NotificationBell } from "./NotificationBell";
import { CommandPalette } from "./CommandPalette";
import { MqttDegradedBanner } from "./MqttDegradedBanner";
import { AppMobileDrawer } from "./AppMobileDrawer";
import { AssistantDock } from "@/features/assistant/AssistantDock";
import { AssistantFab } from "@/features/assistant/AssistantFab";
import { AssistantLauncher } from "@/features/assistant/AssistantLauncher";
import { useAssistantReflow } from "@/features/assistant/hooks/useAssistantReflow";
import { useAuthStore } from "@/stores/auth.store";
import { useLogout } from "@/features/auth/hooks";
import { getUserRoleLabel } from "@/lib/display-labels";
import { useRealtimeConnection } from "@/lib/use-realtime-connection";
import { useRealtimeInvalidate } from "@/lib/use-realtime-invalidate";
import { cn } from "@/lib/utils";

interface NavItem {
  to: string;
  label: string;
  icon: typeof Activity;
  group: "Operação" | "Cadastro" | "Análise" | "Admin";
  gestorOnly?: boolean;
}

const NAV_ITEMS: NavItem[] = [
  { to: "/", label: "Dashboard", icon: LayoutDashboard, group: "Operação" },
  { to: "/lines", label: "Linhas", icon: Activity, group: "Operação" },
  { to: "/orders", label: "Ordens de Produção", icon: CalendarClock, group: "Operação" },
  { to: "/data-entry", label: "Entrada de dados", icon: ClipboardList, group: "Operação" },
  // Spec 083 — leitura do que a linha entregou no período. Fica em Operação, e não em Análise,
  // porque é consulta de rotina do turno, não estudo.
  {
    to: "/controle-de-producao",
    label: "Controle de produção",
    icon: ClipboardCheck,
    group: "Operação",
  },
  { to: "/products", label: "Mix de produtos", icon: Package, group: "Cadastro" },
  { to: "/devices", label: "CLPs & Sensores", icon: Cpu, group: "Cadastro", gestorOnly: true },
  // Spec 078 — biblioteca de modelos 3D: leitura para todos, ações de gestão só na página.
  { to: "/modelos-3d", label: "Modelos 3D", icon: Box, group: "Cadastro" },
  { to: "/alarms", label: "Alarmes", icon: AlertCircle, group: "Análise" },
  { to: "/simulations", label: "Simulações", icon: BarChart3, group: "Análise" },
  // Spec 077 — as filas do ciclo fechado. UMA LINHA em `NAV_ITEMS`, que é DADO: a fence do
  // Sidebar é `ui/sidebar.tsx` e o bloco `<Sidebar>`, e a Spec 074 é o precedente. `gestorOnly`
  // já satisfaz FR-046 — `visibleItems` filtra antes de renderizar, sem código de permissão novo.
  {
    to: "/ciclo-fechado",
    label: "Ciclo fechado",
    icon: RefreshCcwDot,
    group: "Análise",
    gestorOnly: true,
  },
  // Spec 074 — o expediente da fábrica é configuração de gestão, e `gestorOnly` já satisfaz
  // FR-024: a entrada só aparece para quem pode usá-la.
  { to: "/organizacao", label: "Organização", icon: Building2, group: "Admin", gestorOnly: true },
  { to: "/users", label: "Usuários", icon: Users, group: "Admin", gestorOnly: true },
  { to: "/audit", label: "Auditoria", icon: History, group: "Admin", gestorOnly: true },
];

const GROUP_ORDER: NavItem["group"][] = ["Operação", "Cadastro", "Análise", "Admin"];

function isActive(currentPath: string, itemPath: string): boolean {
  if (itemPath === "/") return currentPath === "/";
  return currentPath === itemPath || currentPath.startsWith(`${itemPath}/`);
}

export function AppLayout() {
  const user = useAuthStore((s) => s.user)!;
  const logout = useLogout();
  const navigate = useNavigate();
  const location = useLocation();
  const [drawerOpen, setDrawerOpen] = useState(false);
  // Ambos usam useSocketEffect (religam quando o socket aparece/troca), então a ordem
  // entre eles é indiferente: a conexão abre o socket e a invalidação se religa sozinha.
  useRealtimeConnection();
  useRealtimeInvalidate();

  // Reflow do <main> quando o dock abre (nunca cobre o dado). O lançador do Topbar e a
  // hotkey mod+j vivem em <AssistantLauncher/>.
  const assistantReflow = useAssistantReflow();

  const visibleItems = NAV_ITEMS.filter((i) => !i.gestorOnly || user.role === "GESTOR");
  const byGroup = GROUP_ORDER.map((g) => ({
    group: g,
    items: visibleItems.filter((i) => i.group === g),
  })).filter((b) => b.items.length > 0);

  const env = (import.meta as unknown as { env: Record<string, string | undefined> }).env;
  const envLabel = env?.MODE === "production" ? undefined : "dev";

  function handleNav(to: string) {
    setDrawerOpen(false);
    navigate(to);
  }

  const sidebar = (
    <Sidebar className="h-full">
      <SidebarBrand
        name="Opus DT"
        env={envLabel}
        mark={<img src="/logo-opus-dt-mark.png" alt="" aria-hidden="true" className="h-10" />}
      />
      {byGroup.map(({ group, items }) => (
        <SidebarGroup key={group} label={group}>
          {items.map((it) => (
            <SidebarNavItem
              key={it.to}
              label={it.label}
              icon={<it.icon className="h-4 w-4" />}
              active={isActive(location.pathname, it.to)}
              onClick={() => handleNav(it.to)}
            />
          ))}
        </SidebarGroup>
      ))}
      <div className="mt-auto" />
      <div className="flex items-center gap-2.5 border-t border-border-inverse px-3 py-3">
        <div className="grid h-7 w-7 place-items-center rounded-full bg-bg-inverse-muted text-[12px] font-semibold text-text-inverse">
          {(user.name?.[0] ?? "?").toUpperCase()}
        </div>
        <div className="min-w-0 flex-1">
          <div className="truncate text-[12px] font-medium text-text-inverse">{user.name}</div>
          <div className="text-[11px] text-text-inverse-secondary">
            {getUserRoleLabel(user.role)}
          </div>
        </div>
        <Button
          variant="ghost"
          size="icon"
          onClick={() => logout.mutate()}
          aria-label="Sair da sessão"
          className="text-text-inverse-secondary hover:bg-bg-inverse-muted"
        >
          <LogOut className="h-4 w-4" />
        </Button>
      </div>
    </Sidebar>
  );

  const sectionCrumb = (() => {
    const seg = location.pathname.split("/").filter(Boolean)[0];
    if (!seg) return "Dashboard";
    const match = NAV_ITEMS.find((i) => i.to === `/${seg}` || (seg === "" && i.to === "/"));
    return match?.label ?? seg.charAt(0).toUpperCase() + seg.slice(1);
  })();

  return (
    <div className="flex min-h-screen bg-bg-app">
      <a
        href="#main"
        className="sr-only focus:not-sr-only focus:absolute focus:left-2 focus:top-2 focus:z-50 focus:rounded focus:bg-primary focus:px-3 focus:py-2 focus:text-primary-foreground"
      >
        Pular para o conteúdo principal
      </a>

      {}
      <div className="hidden lg:block">{sidebar}</div>

      {}
      <AppMobileDrawer open={drawerOpen} onClose={() => setDrawerOpen(false)}>
        {sidebar}
      </AppMobileDrawer>

      <div
        className={cn("flex min-w-0 flex-1 flex-col", assistantReflow.className)}
        style={assistantReflow.style}
      >
        <Topbar
          crumbs={[sectionCrumb]}
          actions={
            <>
              <Button
                variant="ghost"
                size="icon"
                className="lg:hidden"
                aria-label="Abrir menu"
                onClick={() => setDrawerOpen(true)}
              >
                <Menu className="h-4 w-4" />
              </Button>
              <AssistantLauncher />
              <ThemeToggle />
              <RealtimeBadge />
              <NotificationBell />
            </>
          }
        />
        <MqttDegradedBanner />
        <main id="main" className="flex-1 bg-bg-app p-4 md:p-6">
          <Outlet />
        </main>
      </div>

      <CommandPalette />
      <AssistantDock />
      <AssistantFab />
    </div>
  );
}
